import type { ReactNode } from "react";
import type { Direction } from "../../shared/types";
import { formatPct, returnColor, useCountUp } from "../lib/format";

export function Card({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`panel p-4 ${className}`}>{children}</div>;
}

export function SectionHeader({
  title,
  right,
  className = "",
}: {
  title: ReactNode;
  right?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`mb-3 flex items-center justify-between ${className}`}>
      <h2 className="text-sm font-semibold uppercase tracking-widest text-muted">
        {title}
      </h2>
      {right}
    </div>
  );
}

/** Big, color-coded, monospace return with optional smooth count-up. */
export function ReturnValue({
  value,
  className = "",
  digits = 2,
  animate = true,
  showArrow = false,
}: {
  value: number;
  className?: string;
  digits?: number;
  animate?: boolean;
  showArrow?: boolean;
}) {
  const animated = useCountUp(animate ? value : value, animate ? 600 : 0);
  const shown = animate ? animated : value;
  return (
    <span className={`num font-bold ${returnColor(value)} ${className}`}>
      {showArrow && value !== 0 ? (value > 0 ? "▲ " : "▼ ") : ""}
      {formatPct(shown, digits)}
    </span>
  );
}

const RANK_RING: Record<number, string> = {
  1: "border-gold text-gold shadow-[0_0_18px_rgba(247,201,72,0.35)]",
  2: "border-silver text-silver",
  3: "border-bronze text-bronze",
};

export function RankBadge({ rank }: { rank: number }) {
  const style = RANK_RING[rank] ?? "border-edge text-muted";
  return (
    <span className="relative inline-flex">
      {rank === 1 && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-sm leading-none text-gold">
          ♛
        </span>
      )}
      <span
        className={`num inline-flex h-9 w-9 items-center justify-center rounded-full border-2 bg-ink-900 text-lg font-bold ${style}`}
      >
        {rank}
      </span>
    </span>
  );
}

export function StatTile({
  label,
  value,
  sub,
  tone = "text-fg",
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: string;
}) {
  return (
    <div className="panel px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-faint">
        {label}
      </div>
      <div className={`num mt-1 text-2xl font-bold leading-tight ${tone}`}>
        {value}
      </div>
      {sub && <div className="mt-0.5 truncate text-[11px] text-muted">{sub}</div>}
    </div>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`skeleton rounded-lg ${className}`} />;
}

export function DeltaArrow({ delta }: { delta: number }) {
  if (delta === 0) {
    return <span className="num text-xs text-faint">—</span>;
  }
  const up = delta > 0;
  return (
    <span
      className={`num inline-flex items-center gap-0.5 text-xs font-semibold ${
        up ? "text-gain" : "text-loss"
      }`}
      title={up ? `Up ${delta}` : `Down ${-delta}`}
    >
      {up ? "▲" : "▼"}
      {Math.abs(delta)}
    </span>
  );
}

export function DirectionTag({
  direction,
  size = "md",
}: {
  direction: Direction;
  size?: "sm" | "md";
}) {
  const long = direction === "LONG";
  const pad = size === "sm" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs";
  return (
    <span
      className={`inline-flex items-center rounded font-bold uppercase tracking-wide ${pad} ${
        long ? "bg-gain/15 text-gain" : "bg-loss/15 text-loss"
      }`}
    >
      {direction}
    </span>
  );
}

export function Pill({
  children,
  tone = "neutral",
  className = "",
}: {
  children: ReactNode;
  tone?: "neutral" | "live" | "warn" | "accent";
  className?: string;
}) {
  const tones: Record<string, string> = {
    neutral: "bg-ink-700 text-muted",
    live: "bg-loss/20 text-lossbright",
    warn: "bg-clock/20 text-clock",
    accent: "bg-accent/20 text-accent",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

export function LivePill({ live }: { live: boolean }) {
  if (!live) {
    return (
      <Pill tone="neutral">
        <span className="h-2 w-2 rounded-full bg-faint" />
        CLOSED
      </Pill>
    );
  }
  return (
    <Pill tone="live">
      <span className="h-2 w-2 animate-pulseclock rounded-full bg-lossbright" />
      LIVE
    </Pill>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 text-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-edge border-t-accent" />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-edge p-8 text-center text-muted">
      {children}
    </div>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-loss/40 bg-loss/10 px-4 py-3 text-sm text-lossbright">
      {message}
    </div>
  );
}
